// src/components/PurchaseButton.jsx
import React, { useState } from 'react';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { calculateFees } from '../services/stripe';
import { COLORS, LAYOUT } from '../constants';
import StripeCheckout from './StripeCheckout';

// Modern SVG Icons
const ShoppingBagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22,4 12,14.01 9,11.01"></polyline>
  </svg>
);

function PurchaseButton({ listing, className = '', compact = false }) {
  const [showCheckout, setShowCheckout] = useState(false);
  const [purchaseConfirmed, setPurchaseConfirmed] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const user = auth.currentUser;

  // Don't show purchase button if user is the seller or not logged in
  if (!user || !listing || (listing.sellerId === user.uid || listing.createdBy === user.uid) || listing.status === 'sold') {
    return null;
  }

  // Check if item has quantity tracking
  const hasQuantity = listing.quantityAvailable !== undefined;
  const isOutOfStock = hasQuantity && listing.quantityAvailable <= 0;
  
  // Determine quantity selection method based on available quantity
  const getQuantitySelectionType = () => {
    if (!hasQuantity || listing.quantityAvailable <= 1) return 'none';
    if (listing.quantityAvailable <= 10) return 'dropdown';
    if (listing.quantityAvailable <= 50) return 'smart-dropdown';
    return 'input';
  };
  
  const quantitySelectionType = getQuantitySelectionType();

  const totalPrice = listing.price * selectedQuantity;
  const fees = calculateFees(totalPrice);

  const handlePurchaseClick = async () => {
    if (!user.emailVerified) {
      alert('Please verify your email before making a purchase.');
      return;
    }
    
    // Check if seller is properly set up BEFORE showing checkout
    try {
      const sellerId = listing.sellerId || listing.createdBy;
      const sellerDoc = await getDoc(doc(db, 'users', sellerId));
      
      if (!sellerDoc.exists()) {
        alert('Error: Seller account not found. Please contact support.');
        return;
      }
      
      const sellerData = sellerDoc.data();
      
      if (!sellerData.stripeConnectAccountId) {
        alert(`This seller hasn't set up their payment account yet. They need to complete Stripe onboarding before you can purchase.\n\nPlease contact the seller: ${sellerData.email || 'No email on file'}`);
        return;
      }
      
      if (sellerData.sellerStatus !== 'active') {
        alert(`This seller's account is not active (status: ${sellerData.sellerStatus || 'unknown'}). They need to complete account verification.\n\nPlease contact the seller: ${sellerData.email || 'No email on file'}`);
        return;
      }
      
      // All checks passed - show checkout
      setShowCheckout(true);
      } catch (error) {
      alert('Error validating seller account. Please try again or contact support.');
    }
  };

  const handlePurchaseSuccess = (result) => {
    setPurchaseConfirmed(true);
    setShowCheckout(false);
    
    setTimeout(() => {
      const totalPrice = listing.price * selectedQuantity;
      const totalFees = calculateFees(totalPrice);
      alert(`Purchase successful! You've bought ${selectedQuantity} x "${listing.title}" for $${totalFees.total}. Check your email for receipt details.`);
      window.location.reload();
    }, 500);
  };

  const handlePurchaseCancel = () => {
    setShowCheckout(false);
  };

  if (purchaseConfirmed) {
    return (
      <div style={buttonStyles.confirmed}>
        <CheckCircleIcon />
        <span>Purchase Complete!</span>
      </div>
    );
  }

  // Helper function to generate quantity options for smart dropdown
  const generateSmartQuantityOptions = () => {
    const options = [];
    const available = listing.quantityAvailable;
    
    // Always include 1-10 if available
    for (let i = 1; i <= Math.min(10, available); i++) {
      options.push(i);
    }
    
    // Add increments of 5 beyond 10
    if (available > 10) {
      for (let i = 15; i <= available; i += 5) {
        options.push(i);
      }
      // Always include the max if it's not already included
      if (!options.includes(available) && available > options[options.length - 1]) {
        options.push(available);
      }
    }
    
    return options;
  };

  // Helper function to validate quantity input
  const handleQuantityInputChange = (value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1) {
      setSelectedQuantity(1);
    } else if (num > listing.quantityAvailable) {
      setSelectedQuantity(listing.quantityAvailable);
    } else {
      setSelectedQuantity(num);
    }
  };

  return (
    <div style={buttonStyles.container}>
      {quantitySelectionType !== 'none' && !isOutOfStock && (
        <div 
          style={buttonStyles.quantitySelector}
          onClick={(e) => e.stopPropagation()}
        >
          <label style={buttonStyles.quantityLabel}>
            Quantity: {listing.quantityAvailable} available
          </label>
          
          {quantitySelectionType === 'dropdown' && (
            <select 
              value={selectedQuantity}
              onChange={(e) => setSelectedQuantity(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={buttonStyles.quantitySelect}
            >
              {[...Array(listing.quantityAvailable)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1} {i + 1 === 1 ? 'item' : 'items'} (${((i + 1) * listing.price).toFixed(2)})
                </option>
              ))}
            </select>
          )}
          
          {quantitySelectionType === 'smart-dropdown' && (
            <select 
              value={selectedQuantity}
              onChange={(e) => setSelectedQuantity(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              style={buttonStyles.quantitySelect}
            >
              {generateSmartQuantityOptions().map((quantity) => (
                <option key={quantity} value={quantity}>
                  {quantity} {quantity === 1 ? 'item' : 'items'} (${(quantity * listing.price).toFixed(2)})
                </option>
              ))}
            </select>
          )}
          
          {quantitySelectionType === 'input' && (
            <div style={buttonStyles.quantityInputContainer}>
              <input
                type="number"
                value={selectedQuantity}
                onChange={(e) => handleQuantityInputChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => handleQuantityInputChange(e.target.value)}
                min="1"
                max={listing.quantityAvailable}
                style={buttonStyles.quantityInput}
                placeholder="Enter quantity"
              />
              <div style={buttonStyles.quantityInputHelper}>
                Max: {listing.quantityAvailable} • Total: ${(selectedQuantity * listing.price).toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}
      
      <button 
        onClick={handlePurchaseClick}
        style={{
          ...buttonStyles.base,
          ...(compact ? buttonStyles.compact : buttonStyles.full),
          ...(listing.status === 'sold' || isOutOfStock ? buttonStyles.disabled : {}),
        }}
        disabled={listing.status === 'sold' || isOutOfStock}
        onMouseEnter={(e) => {
          if (listing.status !== 'sold' && !isOutOfStock) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
          }
        }}
        onMouseLeave={(e) => {
          if (listing.status !== 'sold' && !isOutOfStock) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
          }
        }}
      >
        <ShoppingBagIcon />
        <div style={compact ? buttonStyles.contentCompact : buttonStyles.content}>
          <div style={buttonStyles.text}>
            {isOutOfStock ? 'Out of Stock' : compact ? 'Buy Now' : 'Buy Now'}
          </div>
          {!isOutOfStock && (
            <div style={buttonStyles.price}>
              ${fees.total}
              {hasQuantity && selectedQuantity > 1 && (
                <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                  {' '}({selectedQuantity} items)
                </span>
              )}
            </div>
          )}
        </div>
      </button>

      {showCheckout && (
        <>
          <StripeCheckout
            key={`${listing.id}-${selectedQuantity}-${Date.now()}`}
            listing={listing}
            quantity={selectedQuantity}
            onSuccess={handlePurchaseSuccess}
            onCancel={handlePurchaseCancel}
          />
        </>
      )}
    </div>
  );
};

const buttonStyles = {
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
  },
  base: {
    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
    color: COLORS.white,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    width: '100%',
    justifyContent: 'center',
    boxShadow: `0 4px 12px ${COLORS.primary}30`,
    transform: 'translateY(0)',
  },
  compact: {
    padding: `${LAYOUT.spacing.sm} ${LAYOUT.spacing.md}`,
  },
  full: {
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
  },
  disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    background: COLORS.gray,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
  },
  contentCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xs,
  },
  text: {
    fontWeight: '600',
    fontSize: '0.875rem',
  },
  price: {
    fontWeight: '700',
    fontSize: '0.875rem',
  },
  confirmed: {
    background: `linear-gradient(135deg, ${COLORS.success}, #43a047)`,
    color: COLORS.white,
    borderRadius: LAYOUT.borderRadius.md,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    fontWeight: '600',
    width: '100%',
    justifyContent: 'center',
  },
  quantitySelector: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.md,
    padding: `${LAYOUT.spacing.sm} ${LAYOUT.spacing.md}`,
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.sm,
    border: `1px solid ${COLORS.border}`,
  },
  quantityLabel: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    whiteSpace: 'nowrap',
  },
  quantitySelect: {
    flex: 1,
    padding: `${LAYOUT.spacing.sm} ${LAYOUT.spacing.md}`,
    fontSize: '0.875rem',
    fontWeight: '500',
    border: `1px solid ${COLORS.border}`,
    borderRadius: LAYOUT.borderRadius.sm,
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    cursor: 'pointer',
    outline: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23CE1126' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    paddingRight: '32px',
    appearance: 'none',
  },
  quantityInputContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xs,
  },
  quantityInput: {
    padding: `${LAYOUT.spacing.sm} ${LAYOUT.spacing.md}`,
    fontSize: '0.875rem',
    fontWeight: '500',
    border: `2px solid ${COLORS.border}`,
    borderRadius: LAYOUT.borderRadius.sm,
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    outline: 'none',
    transition: 'border-color 0.2s ease',
    ':focus': {
      borderColor: COLORS.primary,
    },
  },
  quantityInputHelper: {
    fontSize: '0.75rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },
};

export default PurchaseButton;